import { useState, useEffect } from "react";
import ReportPDF from "./ATRReport"; // Import the new component
import { questions, riskCategories } from "./Questions";
import "../../../css/ATRProfiler/ATRProfiler.css";
import Sidebar from "../../../frontend/DesignSystem/Sidebar";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import InputField from "../../../frontend/DesignSystem/InputField";
import "../../../css/Components/DesignSystem/RadioButton.css";
import ButtonYellow from "../../../frontend/DesignSystem/Buttons/ButtonYellow";
import { pdf } from "@react-pdf/renderer";
import RiskAdverse from "../../../assets/images/ATR Profiler/Risk Averse.jpg";
import Conservative from "../../../assets/images/ATR Profiler/Conservative.jpg";
import Balanced from "../../../assets/images/ATR Profiler/Balanced.jpg";
import Moderate from "../../../assets/images/ATR Profiler/Moderate.jpg";
import Dynamic from "../../../assets/images/ATR Profiler/Dynamic.jpg";
import Adventurous from "../../../assets/images/ATR Profiler/Adventurous.png";
import PortalCard from "../../../frontend/DesignSystem/Cards/PortalCard";
import { collection, query, where, getDocs } from "firebase/firestore";
import { firestore, auth, storage } from "../../../backend/firebaseconfig";
import { getDownloadURL, ref } from "firebase/storage";
import { useUserEmail } from "../../../context/UserEmailContext";
import { useUserRole } from "../../../context/UserRoleContext";
import { Helmet } from "react-helmet-async";
import { useBranding } from "../../../context/BrandingContext";
import generateATRReportsForAllAdvisers from "./helperFunctions/generateATRReportsForTesting";

/**
 * ATRReport is a React functional component that renders the Attitude to Risk (ATR) Questionnaire.
 *
 * This component allows advisers to assess a customer's risk profile by answering a series of questions.
 * It calculates a risk category based on the responses, allows manual override of the category,
 * and generates a downloadable PDF report with branding and risk descriptions.
 *
 * Features:
 * - Renders a dynamic questionnaire based on the `questions` array.
 * - Calculates total score and determines the risk category.
 * - Allows selection of a custom risk category.
 * - Displays risk descriptions and statistics for the selected category.
 * - Collects customer and adviser names.
 * - Fetches adviser branding (trading name, logo, primary color) from Firestore.
 * - Generates and downloads a PDF report with all relevant data.
 * - Provides UI feedback for incomplete forms and loading states.
 *
 * @component
 * @returns {JSX.Element} The rendered ATR questionnaire and report generation UI.
 */
export default function ATRReport() {
  const breadcrumbItems = [
    { label: "Tools & Calculators", to: "/portal/tools-calculators" },
    { label: "ATR Profiler" },
  ];

  const [responses, setResponses] = useState({});
  const [totalScore, setTotalScore] = useState(0);
  const [riskCategory, setRiskCategory] = useState("");
  // const [customCategory, setCustomCategory] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [formKey, setFormKey] = useState(0); // Add a key for the form
  const [adviserName, setAdviserName] = useState(
    auth.currentUser ? auth.currentUser.displayName : "",
  );
  const [customCategory, setCustomCategory] = useState(
    riskCategories.length > 0 ? riskCategories[0].category : "",
  );
  const [loading, setLoading] = useState(false);
  const { userEmail } = useUserEmail();
  const { userRole } = useUserRole();

  const { tradingName, primaryColor, logo } = useBranding();
  const cssPrimary = `#${primaryColor}`; // UI needs valid CSS color; provider keeps raw hex (no '#').

  // console.log("User email:", userEmail);

  /**
   * Returns the current date formatted as "day month year" (e.g., "7 June 2024")
   * using the "en-GB" locale.
   *
   * @returns {string} The formatted current date string.
   */
  const formatDate = () => {
    const today = new Date();
    const options = { day: "numeric", month: "long", year: "numeric" };
    return today.toLocaleDateString("en-GB", options);
  };

  /**
   * Resets the ATR Profiler form to its initial state.
   *
   * - Clears all user responses.
   * - Resets the total score to 0.
   * - Clears the risk category, customer name, and adviser name fields.
   * - Sets the adviser name to the current user's display name if available.
   * - Sets the custom risk category to the first available category, if any.
   * - Updates the form key to force a re-render of the form.
   */
  const resetForm = () => {
    setResponses({});
    setTotalScore(0);
    setRiskCategory("");
    setCustomerName("");
    setAdviserName(!auth.currentUser ? auth.currentUser.displayName : "");
    setCustomCategory(
      riskCategories.length > 0 ? riskCategories[0].category : "",
    );
    setFormKey((prevKey) => prevKey + 1); // Update the key to force re-render
  };

  // useEffect(() => {
  //   fetchAdviserBranding();
  // }, []);

  /**
   * Handles the change event for a question's response.
   *
   * Updates the responses state with the new score and label for the given question index,
   * recalculates the total score, and determines the risk category based on the updated total score.
   * Also updates the custom category state accordingly.
   *
   * @param {number} qIndex - The index of the question being updated.
   * @param {number} score - The score selected for the question.
   * @param {string} label - The label associated with the selected score.
   */
  const handleChange = (qIndex, score, label) => {
    const updatedResponses = { ...responses, [qIndex]: { score, label } };
    setResponses(updatedResponses);

    const newTotalScore = Object.values(updatedResponses).reduce(
      (acc, val) => acc + val.score,
      0,
    );
    setTotalScore(newTotalScore);

    const category = riskCategories.find(
      (cat) => newTotalScore >= cat.low && newTotalScore <= cat.high,
    );
    setRiskCategory(category ? category.category : "Unknown");
    setCustomCategory(category ? category.category : "Unknown");
  };

  const categoryImages = {
    "Risk Averse": RiskAdverse,
    Conservative: Conservative,
    Balanced: Balanced,
    Moderate: Moderate,
    Dynamic: Dynamic,
    Adventurous: Adventurous,
  };

  const imageSrc = categoryImages[customCategory];

  /**
   * Fetches branding information for an adviser based on the user's email and role.
   *
   * This function queries the Firestore database for an adviser document matching the provided userEmail
   * within the collection path `Authorized Members/{userRole}/Data`. If found, it extracts the adviser's
   * trading name and primary color, and attempts to retrieve the adviser's logo from Firebase Storage.
   * If any value is missing or the adviser is not found, default values are returned.
   *
   * @async
   * @function fetchAdviserBranding
   * @returns {Promise<{tradingName: string, logo: string, primaryColor: string}>}
   *   An object containing the adviser's trading name, logo URL, and primary color.
   */
  // const fetchAdviserBranding = async () => {
  //   if (!userEmail)
  //     return { tradingName: "", logo: "", primaryColor: "081e56" };

  //   try {
  //     const advisersCollection = collection(
  //       firestore,
  //       `Authorized Members/${userRole}/Data`
  //     );
  //     const adviserQuery = query(
  //       advisersCollection,
  //       where("email", "==", userEmail)
  //     );
  //     const querySnapshot = await getDocs(adviserQuery);

  //     if (!querySnapshot.empty) {
  //       const adviserData = querySnapshot.docs[0].data();

  //       // Extract values directly
  //       const tradingName =
  //         adviserData.tradingName || "Rosemount Financial Solution (IFA) Ltd";
  //       const primaryColor = adviserData.primaryColor || "081e56";

  //       // Fetch logo
  //       let logoURL = "";
  //       if (tradingName) {
  //         try {
  //           const logoRef = ref(storage, `Logos/${tradingName}.jpg`);
  //           logoURL = await getDownloadURL(logoRef);
  //         } catch (error) {
  //           // console.warn("Logo not found:", error);
  //         }
  //       }

  //       return { tradingName, primaryColor, logo: logoURL };
  //     }
  //   } catch (error) {
  //     // console.error("Error fetching adviser branding:", error);
  //   }

  //   return {
  //     tradingName: "Rosemount Financial Solutions (IFA) Ltd",
  //     logo: "",
  //     primaryColor: "081e56",
  //   };
  // };

  /**
   * Handles the process of generating and downloading a PDF report.
   *
   * This function performs the following steps:
   * 1. Sets the loading state to true to indicate the download process has started.
   * 2. Fetches adviser branding information (trading name, logo, primary color) asynchronously.
   * 3. Creates a memoized React component for the PDF report using the fetched branding and provided props.
   * 4. Waits for a short timeout to ensure the PDF is generated.
   * 5. Converts the React PDF component to a Blob and triggers a download with a filename based on the customer's name.
   * 6. Resets the form and sets the loading state to false after the download is initiated.
   *
   * @async
   * @function handleDownloadPDF
   * @returns {Promise<void>} Resolves when the PDF download process is complete.
   */
  const handleDownloadPDF = async () => {
    setLoading(true);

    // Use fetched data directly instead of relying on setState
    // const { tradingName, logo, primaryColor } = await fetchAdviserBranding();

    // console.log("Using Trading Name:", tradingName);
    // console.log("Using Logo:", logo);
    // console.log("Using Primary Color:", primaryColor);

    // Create the PDF with the fetched values (not the state)
    const memoizedReportPDF = (
      <ReportPDF
        responses={responses}
        totalScore={totalScore}
        riskCategory={riskCategory}
        customCategory={customCategory}
        customerName={customerName}
        adviserName={adviserName}
        questions={questions}
        formatDate={formatDate}
        tradingName={tradingName}
        logo={logo}
        primaryColor={primaryColor}
      />
    );

    // Wait for the PDF to be generated
    setTimeout(async () => {
      const blob = await pdf(memoizedReportPDF).toBlob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `ATR Report - ${customerName}.pdf`;
      link.click();

      resetForm();
      setLoading(false);
    }, 500);
  };

  const handlePreviewPDF = async () => {
    setLoading(true);

    // const { tradingName, logo, primaryColor } = await fetchAdviserBranding();

    const memoizedReport = (
      <ReportPDF
        responses={responses}
        totalScore={totalScore}
        riskCategory={riskCategory}
        customCategory={customCategory}
        customerName={customerName}
        adviserName={adviserName}
        questions={questions}
        formatDate={formatDate}
        tradingName={tradingName}
        logo={logo}
        primaryColor={primaryColor}
      />
    );

    const blob = await pdf(memoizedReport).toBlob();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, "_blank");
    setLoading(false);
  };

  // useEffect(() => {
  //   if (!auth.currentUser) {
  //     navigate("/"); // Redirect to Sign-In if not authenticated
  //   }
  //   if (window.innerWidth <= 839) {
  //     toggleSidebar();
  //     setIsOpen(false);
  //   }
  // }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setCustomCategory(value);
  };

  const riskRatings = [
    {
      riskRating: "Risk Averse",

      potentialReturns: [
        "There are no guarantees with this investment strategy and actual results achieved may be higher or lower than those shown. However, these probable returns are a sensible way of understanding the potential downsides as well as the upsides when saving or investing this way.",
      ],
      potentialGain: [
        "The graph below is an example of the potential returns based on an assumed £100,000 invested for a term of 10 years following the illustrative asset allocation, assuming no charges, no active management and with regular rebalancing. The chart has been simplified, in that it does not show potential daily market fluctuations, or account for your specific financial objectives, nor does it take into consideration the impact of inflation but is an illustration of the variability in long term returns before charges that might be experienced with the illustrative asset allocation:",
      ],
      potentialGrowth: [
        "The top line shows there is a 1 in 10 probability of achieving at least £127,544 after 10 years. This is what you might get back from your original investment if market conditions are very good.",
      ],
      averageGrowth: [
        "The middle line shows there is a 1 in 2 probability of achieving at least £108,006 after 10 years. This is what you might get back from your original investment under average market conditions.",
      ],
      lowerGrowth: [
        "The bottom line shows there is a 1 in 20 probability of achieving less than £93,907. This is what you might get back from your original investment if market conditions are poor.",
      ],
    },
    {
      riskRating: "Conservative",

      potentialReturns: [
        "As an example only, were you to invest in the illustrative asset allocation then the downside risk in a single bad year is estimated as a loss of 7% or worse of your investment before charges (equivalent to a £7,000 loss or worse on £100,000). The likelihood of this happening is estimated as having odds of 1 in 20 each year. A bad year could occur at any time, including the first year of the investment.",
      ],
      potentialGain: [
        "The graph below is an example of the potential returns based on an assumed £100,000 invested for a term of 10 years following the illustrative asset allocation, assuming no charges, no active management and with regular rebalancing. The chart has been simplified, in that it does not show potential daily market fluctuations, or account for your specific financial objectives, but is an illustration of the variability in long term returns before charges that might be experienced with the illustrative asset allocation:",
      ],
      potentialGrowth: [
        "The top line shows there is a 1 in 10 probability of achieving at least £156,617 after 10 years. This is what you might get back from your original investment if market conditions are very good.",
      ],
      averageGrowth: [
        "The middle line shows there is a 1 in 2 probability of achieving at least £126,681 after 10 years. This is what you might get back from your original investment under average market conditions.",
      ],
      lowerGrowth: [
        "The bottom line shows there is a 1 in 20 probability of achieving less than £94,675. This is what you might get back from your original investment if market conditions are poor.",
      ],
    },
    {
      riskRating: "Balanced",

      potentialReturns: [
        "As an example only, were you to invest in the illustrative asset allocation then the downside risk in a single bad year is estimated as a loss of 10% or worse of your investment before charges (equivalent to a £10,000 loss or worse on £100,000). The likelihood of this happening is estimated as having odds of 1 in 20 each year. A bad year could occur at any time, including the first year of the investment.",
      ],
      potentialGain: [
        "The graph below is an example of the potential returns based on an assumed £100,000 invested for a term of 10 years following the illustrative asset allocation, assuming no charges, no active management and with regular rebalancing. The chart has been simplified, in that it does not show potential daily market fluctuations, or account for your specific financial objectives, but is an illustration of the variability in long term returns before charges that might be experienced with the illustrative asset allocation:",
      ],
      potentialGrowth: [
        "The top line shows there is a 1 in 10 probability of achieving at least £187,605 after 10 years. This is what you might get back from your original investment if market conditions are very good.",
      ],
      averageGrowth: [
        "The middle line shows there is a 1 in 2 probability of achieving at least £136,651 after 10 years. This is what you might get back from your original investment under average market conditions.",
      ],
      lowerGrowth: [
        "The bottom line shows there is a 1 in 20 probability of achieving less than £87,488. This is what you might get back from your original investment if market conditions are poor.",
      ],
    },
    {
      riskRating: "Moderate",

      potentialReturns: [
        "As an example only, were you to invest in the illustrative asset allocation then the downside risk in a single bad year is estimated as a loss of 15% or worse of your investment before charges (equivalent to a £15,000 loss or worse on £100,000). The likelihood of this happening is estimated as having odds of 1 in 20 each year. A bad year could occur at any time, including the first year of the investment.",
      ],
      potentialGain: [
        "The graph below is an example of the potential returns based on an assumed £100,000 invested for a term of 10 years following the illustrative asset allocation, assuming no charges, no active management and with regular rebalancing.",
        "The chart has been simplified, in that it does not show potential daily market fluctuations, or account for your specific financial objectives, but is an illustration of the variability in long term returns before charges that might be experienced with the illustrative asset allocation:",
      ],
      potentialGrowth: [
        "The top line shows there is a 1 in 10 probability of achieving at least £223,258 after 10 years. This is what you might get back from your original investment if market conditions are very good.",
      ],
      averageGrowth: [
        "The middle line shows there is a 1 in 2 probability of achieving at least £145,842 after 10 years. This is what you might get back from your original investment under average market conditions.",
      ],
      lowerGrowth: [
        "The bottom line shows there is a 1 in 20 probability of achieving less than £78,198. This is what you might get back from your original investment if market conditions are poor.",
      ],
    },
    {
      riskRating: "Dynamic",

      potentialReturns: [
        "As an example only, were you to invest in the illustrative asset allocation then the downside risk in a single bad year is estimated as a loss of 20% or worse of your investment before charges (equivalent to a £20,000 loss or worse on £100,000). The likelihood of this happening is estimated as having odds of 1 in 20 each year. A bad year could occur at any time, including the first year of the investment.",
      ],
      potentialGain: [
        "The graph below is an example of the potential returns based on an assumed £100,000 invested for a term of 10 years following the illustrative asset allocation, assuming no charges, no active management and with regular rebalancing.",
        "The chart has been simplified, in that it does not show potential daily market fluctuations, or account for your specific financial objectives, but is an illustration of the variability in long term returns before charges that might be experienced with the illustrative asset allocation:",
      ],
      potentialGrowth: [
        "The top line shows there is a 1 in 10 probability of achieving at least £261,233 after 10 years. This is what you might get back from your original investment if market conditions are very good.",
      ],
      averageGrowth: [
        "The middle line shows there is a 1 in 2 probability of achieving at least £153,700 after 10 years. This is what you might get back from your original investment under average market conditions.",
      ],
      lowerGrowth: [
        "The bottom line shows there is a 1 in 20 probability of achieving less than £69,429. This is what you might get back from your original investment if market conditions are poor.",
      ],
    },
    {
      riskRating: "Adventurous",

      potentialReturns: [
        "As an example only, were you to invest in the illustrative asset allocation then the downside risk in a single bad year is estimated as a loss of 25% or worse of your investment before charges (equivalent to a £25,000 loss or worse on £100,000). The likelihood of this happening is estimated as having odds of 1 in 20 each year. A bad year could occur at any time, including the first year of the investment.",
      ],
      potentialGain: [
        "The graph below is an example of the potential returns based on an assumed £100,000 invested for a term of 10 years following the illustrative asset allocation, assuming no charges, no active management and with regular rebalancing.",
        "The chart has been simplified, in that it does not show potential daily market fluctuations, or account for your specific financial objectives, but is an illustration of the variability in long term returns before charges that might be experienced with the illustrative asset allocation:",
      ],
      potentialGrowth: [
        "The top line shows there is a 1 in 10 probability of achieving at least £316,919 after 10 years. This is what you might get back from your original investment if market conditions are very good.",
      ],
      averageGrowth: [
        "The middle line shows there is a 1 in 2 probability of achieving at least £163,454 after 10 years. This is what you might get back from your original investment under average market conditions.",
      ],
      lowerGrowth: [
        "The bottom line shows there is a 1 in 20 probability of achieving less than £60,865. This is what you might get back from your original investment if market conditions are poor.",
      ],
    },
  ];

  // useEffect(() => {
  //   generateATRReportsForAllAdvisers();
  // }, []);

  return (
    <div className={`ATR-Page `}>
      <Helmet>
        <title>Attitude to Risk Profiler</title>
        <meta
          name="description"
          content="Rosemount Financial Solutions (IFA) Ltd - Adviser Portal"
        />
      </Helmet>
      <Sidebar />

      <div className="ATR-Container">
        <Breadcrumb items={breadcrumbItems} />

        {/* <ButtonYellow onClick={handlePreviewPDF} buttonText="Preview PDF" /> */}
        <h2>Attitude to Risk Questionnaire</h2>

        {/* Questions */}
        <div className="ATR-Content">
          {questions.map((question, qIndex) => (
            <div key={qIndex} className="ATR-Question">
              <div className="Content-Large">{question.text}</div>
              <div className="ATROptions-container">
                {question.options.map((option, oIndex) => (
                  <div className="RadioButton-Container" key={oIndex}>
                    <label className="Radio-Option">
                      <input
                        type="radio"
                        name={`question-${qIndex}`}
                        value={option.label}
                        checked={
                          responses[qIndex]?.label === option.label || false
                        }
                        onChange={() =>
                          handleChange(qIndex, option.score, option.label)
                        }
                      />
                      <span className="Custom-RadioButton"></span>
                      <div className="Content">{option.label}</div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="ATRResult-Container">
          <div className="ATR-Category-Container">
            <div className="SubTitle">Calculated Risk Category:</div>
            <div className="SubTitle-SemiBold">
              {riskCategory || "Not Yet Calculated"}
            </div>
          </div>
          {/* Option to Select a Custom Risk Category */}
          <div className="ATR-Choose-Category-Container">
            <div className="SubTitle">Choose a Different Risk Category: </div>
            <select onChange={handleCategoryChange} value={customCategory}>
              {riskCategories.map((cat, index) => (
                <option className="Content" key={index} value={cat.category}>
                  {cat.category}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="Category-Picker-Container">
          {riskCategories.map((cat, index) => {
            const isActive = customCategory === cat.category; // Check if the card is active
            return (
              <PortalCard
                key={index}
                title={cat.category}
                isAdviser={false}
                onClick={() => setCustomCategory(cat.category)}
                style={{
                  backgroundColor: isActive ? "#081e56" : "#FFF", // Change background color for active card
                  height: "120px", // Reduce height for active card
                  cursor: "pointer", // Add pointer for better UX
                  minWidth: "180px",
                  maxWidth: "180px",
                }}
                titleStyle={{
                  color: isActive ? "#ffffff" : "", // Change text color for active card
                }}
              />
            );
          })}
        </div>

        <div className="ATR-Chosen-Category-Container">
          <img
            src={imageSrc}
            alt={customCategory}
            className="Chosen Category"
          />

          {riskRatings &&
            riskRatings.map((riskRating) => {
              if (riskRating.riskRating === customCategory) {
                return (
                  <div key={riskRating.riskRating}>
                    <div className="Risk-Description-Container">
                      <div className="Content-SemiBold">
                        {riskRating.riskRating}
                      </div>
                      {riskRating.potentialReturns.map((desc, index) => (
                        <div className="Content" key={index}>
                          {desc}
                        </div>
                      ))}
                    </div>
                    <div className="Risk-Description-Container">
                      <div
                        className="Content-SemiBold"
                        style={{ color: "#00B050" }}
                      >
                        Potential Growth:
                      </div>
                      {riskRating.potentialGrowth.map((desc, index) => (
                        <div className="Content" key={index}>
                          {desc}
                        </div>
                      ))}
                    </div>
                    <div className="Risk-Description-Container">
                      <div
                        className="Content-SemiBold"
                        style={{ color: "#FFC000" }}
                      >
                        Average Growth:
                      </div>
                      {riskRating.averageGrowth.map((desc, index) => (
                        <div className="Content" key={index}>
                          {desc}
                        </div>
                      ))}
                    </div>
                    <div className="Risk-Description-Container">
                      <div
                        className="Content-SemiBold"
                        style={{ color: "#FF0000" }}
                      >
                        Lower Growth:
                      </div>
                      {riskRating.lowerGrowth.map((desc, index) => (
                        <div className="Content" key={index}>
                          {desc}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
            })}
        </div>
        <form className="ATRForm-Container" onSubmit={handleSubmit}>
          <InputField
            title="Customer Name*"
            placeholder="Name"
            name="customerName"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            // required={customerName === "" ? true : false}
            isTextarea={false}
          />
          <InputField
            title="Adviser Name*"
            placeholder="Name"
            name="adviserName"
            value={adviserName}
            onChange={(e) => setAdviserName(e.target.value)}
            // required={adviserName === "" ? true : false}
            isTextarea={false}
          />
        </form>

        {Object.keys(responses).length === questions.length &&
        customerName &&
        adviserName ? (
          <ButtonYellow
            onClick={handleDownloadPDF}
            buttonText="Download Report"
          />
        ) : (
          <div className="Content-SemiBold">
            Complete all questions and fill in both Customer and Adviser names
            to enable PDF download.
          </div>
        )}
      </div>
    </div>
  );
}
